import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense } from "react";
import { Phone, MapPin, ShieldCheck, ArrowRight, CheckCircle2 } from "lucide-react";
import { PageHeader } from "./fordon";
import { FyndanuGrid, FyndanuGridSkeleton } from "@/components/FyndanuGrid";
import { fyndanuListingsQuery } from "@/lib/vehicles";
import { buildLocalStockHead } from "@/lib/local-seo";
import type { ListingLike } from "@/lib/listing-seo";

const URL_SELF = "https://www.akhusbilfritid.se/husbil-kalix";

const FAQ = [
  {
    q: "Var kan jag köpa husbil i Kalix?",
    a: "Hos AK Husbil & Fritid på Stabsvägen 5 i Kalix. Vi är familjeägda sedan 2004 och har ett rullande lager av kontrollerade husbilar och husvagnar. Ring 070-862 97 43 för att boka besök.",
  },
  {
    q: "Har ni både nya och begagnade husbilar?",
    a: "Vi fokuserar på kvalitetskontrollerade begagnade husbilar från kända märken som Rapido, Hymer, Kabe, Adria och Bürstner. Alla fordon gås igenom innan de säljs.",
  },
  {
    q: "Kan jag byta in min gamla husbil?",
    a: "Ja, vi tar in inbyten. Ring så bokar vi en tid för värdering — du får ett ärligt bud baserat på skick, ålder och efterfrågan.",
  },
  {
    q: "Hjälper ni med finansiering?",
    a: "Ja, vi samarbetar med etablerade finansbolag och hjälper dig att räkna på vad ett köp faktiskt kostar per månad. Fråga oss direkt så går vi igenom alternativen.",
  },
  {
    q: "Levererar ni husbilar utanför Kalix?",
    a: "Vi säljer till kunder över hela Norrbotten och Sverige. Transport kan ordnas — ring oss så hittar vi en lösning som passar.",
  },
];

export const Route = createFileRoute("/husbil-kalix")({
  loader: ({ context }) => context.queryClient.ensureQueryData(fyndanuListingsQuery),
  // Titel/beskrivning byggs från verkligt lager (antal + prisspann) för högre
  // CTR och starkare lokal relevans. Samma URL och canonical som tidigare.
  head: ({ loaderData }) =>
    buildLocalStockHead({
      category: "husbil",
      items: (loaderData as { items?: ListingLike[] } | undefined)?.items ?? [],
      url: URL_SELF,
      city: "Kalix",
      faq: FAQ,
      breadcrumbName: "Husbil Kalix",
      fallbackBrands: ["Rapido", "Hymer", "Kabe", "Adria", "Bürstner"],
    }),
  component: HusbilKalixPage,
});

function HusbilKalixPage() {
  return (
    <>
      <PageHeader
        label="Husbil · Kalix · Norrbotten"
        title={<>Husbil i <span className="font-italic-display text-accent">Kalix</span>.</>}
        sub="AK Husbil & Fritid är den familjeägda husbilshandlaren i Kalix. Vi säljer kontrollerade husbilar och husvagnar sedan 2004 — och du pratar alltid med samma personer som tar hand om fordonen."
      />

      <section className="container-edge -mt-6 pb-4">
        <div className="flex flex-wrap gap-3">
          <Link to="/fordon" className="inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90">
            Se husbilar i lager <ArrowRight size={14} />
          </Link>
          <a href="tel:0708629743" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            <Phone size={16} /> Ring 070-862 97 43
          </a>
          <Link to="/kontakt" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            <MapPin size={16} /> Boka besök i Kalix
          </Link>
        </div>
      </section>

      <section className="container-edge py-10 md:py-14">
        <p className="eyebrow">Lagret i Kalix just nu</p>
        <h2 className="mt-3 font-display text-3xl md:text-4xl">
          Husbilar och husvagnar <span className="font-italic-display text-accent">på plats</span> hos oss.
        </h2>
        <p className="mt-4 max-w-2xl text-muted-foreground">
          Allt du ser här står i Kalix. Ring innan du åker så ser vi till att fordonet är framme och att någon möter dig.
        </p>
        <div className="mt-8">
          <Suspense fallback={<FyndanuGridSkeleton />}>
            <FyndanuGrid initialLimit={6} hideTrustRow />
          </Suspense>
        </div>
      </section>


      <section className="container-edge py-16 md:py-20">
        <div className="grid gap-12 md:grid-cols-2">
          <div>
            <p className="eyebrow">Varför AK Husbil & Fritid</p>
            <h2 className="mt-3 font-display text-4xl md:text-5xl">Kontrollerade husbilar — inga överraskningar.</h2>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              Vi tror på ärliga affärer. Varje husbil vi tar in i Kalix genomgås av oss innan den läggs ut till försäljning. Vi berättar både det bra och det som kan behöva göras — så du vet exakt vad du köper.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Kontrollerade begagnade husbilar från kända märken",
                "Ärliga priser — inga dolda avgifter",
                "Inbyte och finansieringshjälp",
                "20+ års erfarenhet i Kalix och Norrbotten",
                "Personlig service — samma människor varje gång",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-foreground/85">
                  <CheckCircle2 className="mt-0.5 shrink-0 text-accent" size={20} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl border border-border bg-card p-8 md:p-10">
            <ShieldCheck className="text-accent" size={32} />
            <h3 className="mt-6 font-display text-3xl">Besök oss i Kalix</h3>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              Stabsvägen 5, 952 51 Kalix. Vi öppnar enligt överenskommelse — ring först så säkerställer vi att någon är på plats när du kommer.
            </p>
            <dl className="mt-8 space-y-4 text-sm">
              <div className="flex justify-between border-b border-border pb-3">
                <dt className="text-muted-foreground">Adress</dt>
                <dd className="font-medium">Stabsvägen 5, Kalix</dd>
              </div>
              <div className="flex justify-between border-b border-border pb-3">
                <dt className="text-muted-foreground">Telefon</dt>
                <dd className="font-medium">070-862 97 43</dd>
              </div>
              <div className="flex justify-between border-b border-border pb-3">
                <dt className="text-muted-foreground">E-post</dt>
                <dd className="font-medium">info@akhusbilfritid.se</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Grundat</dt>
                <dd className="font-medium">2004</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      <section className="bg-warm">
        <div className="container-edge py-16 md:py-24">
          <p className="eyebrow">Vanliga frågor</p>
          <h2 className="mt-3 font-display text-4xl md:text-5xl">
            Husbil i <span className="font-italic-display text-accent">Kalix</span> — vad du behöver veta.
          </h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {FAQ.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border bg-card p-6">
                <h3 className="font-display text-xl">{f.q}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-edge py-12">
        <div className="rounded-3xl border border-accent/25 bg-accent/5 p-6 md:p-8">
          <p className="eyebrow text-accent">Läs vidare</p>
          <h2 className="mt-3 font-display text-2xl md:text-3xl leading-tight">
            Så köper du husbil <span className="font-italic-display text-accent">tryggt online</span> 2026
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground max-w-2xl">
            Fuktkontroll, videovisning, öppen köpsumma och leverans från Kalix — vår kompletta guide till hur
            partnerskapet med Fyndanu funkar från annons till nyckelöverlämning.
          </p>
          <Link
            to="/blogg/$slug"
            params={{ slug: "fyndanu-partner-trygg-husbilsaffar-online-2026" }}
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground"
          >
            Läs guiden <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <section className="container-edge py-16 md:py-24">
        <div className="rounded-3xl border border-border bg-foreground p-8 md:p-14 text-background">
          <p className="eyebrow" style={{ color: "hsl(var(--accent))" }}>Nästa steg</p>
          <h2 className="mt-3 font-display text-4xl md:text-6xl leading-[1.05]">Kom förbi eller ring.</h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed opacity-80">
            Titta på lagret först eller ring direkt — vi svarar personligen och hjälper dig att hitta rätt husbil.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/fordon" className="inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3.5 text-sm font-semibold text-accent-foreground hover:opacity-90">
              Se husbilar i lager <ArrowRight size={14} />
            </Link>
            <Link to="/husvagn-kalix" className="inline-flex items-center gap-2 rounded-full border border-background/30 px-6 py-3.5 text-sm font-semibold hover:bg-background/10">
              Husvagnar i Kalix <ArrowRight size={14} />
            </Link>
            <a href="tel:0708629743" className="inline-flex items-center gap-2 rounded-full border border-background/30 px-6 py-3.5 text-sm font-semibold hover:bg-background/10">
              <Phone size={16} /> 070-862 97 43
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
